import type { TicketOrderListItem } from '@pulsify/shared/dto'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Skeleton } from '~/shared/ui/skeleton'
import { countLabel, momentLabel } from '../lib/order-format'
import { pageWindow } from '../lib/order-search'
import { type OrderActions, OrderRowMenu } from './order-actions'
import { BuyerAvatar, BuyerName, EntryCount, OrderStatusChip } from './order-parts'

interface OrderListProps {
  orders: TicketOrderListItem[]
  actions: OrderActions
  onOpen: (order: TicketOrderListItem) => void
  locale: string
  now: Date
}

const HEAD = 'px-3 py-2.5 font-medium first:pl-5 last:pr-4'
const CELL = 'px-3 first:pl-5 last:pr-4'
const SKELETON_ROWS = Array.from({ length: 8 }, (_, index) => `row-${index}`)

export function OrderTable({ orders, actions, onOpen, locale, now }: OrderListProps) {
  return (
    <table className="hidden w-full table-fixed text-sm xl:table">
      <colgroup>
        <col />
        <col className="w-36" />
        <col className="w-16" />
        <col className="w-28" />
        <col className="w-32" />
        <col className="w-20" />
        <col className="w-24" />
        <col className="w-14" />
      </colgroup>
      <thead className="border-border border-b text-left text-muted-foreground text-xs">
        <tr>
          <th scope="col" className={HEAD}>
            {m.ticketPageColumnBuyer()}
          </th>
          <th scope="col" className={HEAD}>
            {m.ticketPageColumnType()}
          </th>
          <th scope="col" className={cn(HEAD, 'text-right')}>
            {m.ticketPageColumnQuantity()}
          </th>
          <th scope="col" className={cn(HEAD, 'text-right')}>
            {m.ticketPageColumnAmount()}
          </th>
          <th scope="col" className={HEAD}>
            {m.ticketPageColumnOrderedAt()}
          </th>
          <th scope="col" className={HEAD}>
            {m.ticketPageFilterCheckIn()}
          </th>
          <th scope="col" className={HEAD}>
            {m.ticketPageFilterStatus()}
          </th>
          <th scope="col" className={HEAD}>
            <span className="sr-only">{m.ticketPageColumnActions()}</span>
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {orders.map((order) => (
          <tr
            key={order.id}
            className="relative h-14 transition-colors duration-150 hover:bg-foreground/[0.03]"
          >
            <td className={CELL}>
              <div className="flex min-w-0 items-center gap-3">
                <BuyerAvatar name={order.buyerName} />
                <div className="flex min-w-0 flex-col">
                  <button
                    type="button"
                    onClick={() => onOpen(order)}
                    className="min-w-0 cursor-pointer truncate text-left outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset"
                  >
                    <BuyerName name={order.buyerName} />
                  </button>
                  {order.buyerEmail ? (
                    <span className="truncate text-muted-foreground text-xs">
                      {order.buyerEmail}
                    </span>
                  ) : null}
                </div>
              </div>
            </td>
            <td className={cn(CELL, 'truncate text-foreground')}>{order.ticketTypeName}</td>
            <td className={cn(CELL, 'text-right text-foreground tabular-nums')}>
              {countLabel(order.quantity, locale)}
            </td>
            <td className={cn(CELL, 'text-right text-foreground tabular-nums')}>
              {formatMoney(order.totalCents, order.currency, locale)}
            </td>
            <td className={cn(CELL, 'truncate text-muted-foreground tabular-nums')}>
              {momentLabel(order.paidAt ?? order.createdAt, locale, now)}
            </td>
            <td className={CELL}>
              <EntryCount order={order} />
            </td>
            <td className={CELL}>
              <OrderStatusChip status={order.status} />
            </td>
            <td className={cn(CELL, 'relative z-10 text-right')}>
              <OrderRowMenu order={order} actions={actions} onOpen={() => onOpen(order)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function OrderCards({ orders, actions, onOpen, locale, now }: OrderListProps) {
  return (
    <ul className="divide-y divide-border xl:hidden">
      {orders.map((order) => (
        <li key={order.id} className="flex items-start gap-1 py-1 pr-2 pl-1 sm:pl-2">
          <button
            type="button"
            onClick={() => onOpen(order)}
            className="flex min-w-0 flex-1 items-start gap-3 rounded-lg px-2 py-2.5 text-left outline-none transition-colors hover:bg-foreground/[0.03] focus-visible:ring-2 focus-visible:ring-ring sm:px-3"
          >
            <BuyerAvatar name={order.buyerName} className="mt-0.5" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm">
                <BuyerName name={order.buyerName} />
                <span className="shrink-0 font-medium text-foreground tabular-nums">
                  {formatMoney(order.totalCents, order.currency, locale)}
                </span>
              </div>
              {order.buyerEmail ? (
                <span className="truncate text-muted-foreground text-xs">{order.buyerEmail}</span>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-0.5 text-muted-foreground text-xs">
                <span className="tabular-nums">
                  {countLabel(order.quantity, locale)} × {order.ticketTypeName}
                </span>
                <span className="tabular-nums">
                  {momentLabel(order.paidAt ?? order.createdAt, locale, now)}
                </span>
                <EntryCount order={order} />
                <OrderStatusChip status={order.status} />
              </div>
            </div>
          </button>
          <div className="pt-2.5">
            <OrderRowMenu order={order} actions={actions} onOpen={() => onOpen(order)} />
          </div>
        </li>
      ))}
    </ul>
  )
}

export function OrderListSkeleton() {
  return (
    <ul aria-hidden className="divide-y divide-border">
      {SKELETON_ROWS.map((key) => (
        <li key={key} className="flex h-14 items-center gap-3 px-4 sm:px-5">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="ml-auto h-3.5 w-16" />
        </li>
      ))}
    </ul>
  )
}

export function OrderPager({
  page,
  pageSize,
  total,
  locale,
  onPage,
}: {
  page: number
  pageSize: number
  total: number
  locale: string
  onPage: (page: number) => void
}) {
  const { from, to, pages } = pageWindow(page, pageSize, total)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-border border-t px-4 py-3 sm:px-5">
      <p className="text-muted-foreground text-sm tabular-nums">
        {m.ticketPageRange({
          from: countLabel(from, locale),
          to: countLabel(to, locale),
          total: countLabel(total, locale),
        })}
      </p>
      {pages > 1 ? (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            <ChevronLeft />
            {m.ticketPagePrevious()}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
            className="has-[>svg]:pr-2.5 has-[>svg]:pl-3"
          >
            {m.ticketPageNext()}
            <ChevronRight />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
